/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import Carousel from 'react-multi-carousel';

import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { searchByCategoryAsync, getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import { isMobile } from '../../components/breakpoints';

import SliderCard from './components/slider-card';
import SliderFilter from './components/slider-filter';

import { IEcommProductSliderViewProps } from './ecomm-product-slider';

/**
 *
 * EcommContentTemplate1View component
 * @extends {React.PureComponent<IEcommProductSliderViewProps>}
 */
export class EcommProductSliderView extends React.PureComponent<IEcommProductSliderViewProps> {
    public state: any = {
        width: 0,
        currCategoryId: 0,
        productList: [],
        hasMounted: false
    };

    public componentDidMount(): void {
        if (this.props.subCategories.length > 0) {
            window.addEventListener('resize', this._updateDimensions);

            this.setState({
                width: window.innerWidth,
                currCategoryId: this.props.subCategories[0].RecordId,
                hasMounted: true
            });
        }
    }

    public render(): JSX.Element | null {
        if (!this.state.hasMounted) {
            return null;
        }

        const { indexes, parentCategoryId, subCategories, config, context, gridSettings } = this.props;

        const responsive = {
            superLargeDesktop: {
                breakpoint: { max: 4000, min: 992 },
                items: 5,
                slidesToSlide: 5
            },
            desktop: {
                breakpoint: { max: 992, min: 768 },
                items: 4,
                slidesToSlide: 4
            },
            tablet: {
                breakpoint: { max: 768, min: 540 },
                items: 4,
                slidesToSlide: 4
            },
            mobile: {
                breakpoint: { max: 540, min: 0 },
                items: 2,
                slidesToSlide: 2
            }
        };

        const mobileMode = isMobile(this.state.width);

        const categoryId = mobileMode ? parentCategoryId : this.state.currCategoryId === 0 ? parentCategoryId : this.state.currCategoryId;

        void searchByCategoryAsync(
            { callerContext: context.actionContext },
            context.request.apiSettings.channelId,
            context.request.apiSettings.catalogId,
            categoryId
        ).then(result => {
            if (result && result.length > 0) {
                const productIds: number[] = [];

                result.map(item => productIds.push(item.RecordId));

                void getByIdsAsync({ callerContext: context.actionContext }, context.request.apiSettings.channelId, productIds).then(ret =>
                    this.setState({ productList: ret })
                );
            }
        });

        const bg = config.bgImage?.src ? { backgroundImage: `url(${config.bgImage.src})` } : { backgroundImage: '' };
        const showArrows = mobileMode ? false : true;
        const spd = config.sliderSpeed ? config.sliderSpeed * 1000 : 5000; // Convert to milliseconds.

        let level = subCategories.find(item => item.RecordId === this.state.currCategoryId)?.Name;

        return (
            <div className='ecomm-product-slider' style={bg}>
                {this._renderTop()}
                {!mobileMode && this._renderSubCategories()}
                <Carousel responsive={responsive} infinite={true} arrows={showArrows} autoPlay={false} autoPlaySpeed={spd}>
                    {this.state.productList.map((item: SimpleProduct, index: number) => {
                        if (mobileMode) {
                            const idx = indexes.findIndex((i: number) => i === index);

                            if (idx !== -1) {
                                level = subCategories[`${idx}`] ? subCategories[`${idx}`].Name : '';
                            }
                        }

                        return (
                            <SliderCard
                                key={index}
                                button_text={config.cardButtonText ? config.cardButtonText : 'Learn More'}
                                category={level ? level : ''}
                                context={this.props.context}
                                enableDescription={config.enableDescription}
                                enableLocation={config.enableLocation}
                                enablePrice={config.enablePrice}
                                gridSettings={gridSettings}
                                product={item}
                                mobileMode={mobileMode}
                            />
                        );
                    })}
                </Carousel>
            </div>
        );
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this._updateDimensions);
    }

    private _renderTop(): JSX.Element | null {
        const { config } = this.props;

        const categoryLink =
            config.categoryLink !== undefined && config.categoryLink.linkUrl.destinationUrl !== ''
                ? config.categoryLink.linkUrl.destinationUrl
                : '';

        const linkText = config.categoryLink !== undefined && config.categoryLink.linkText !== '' ? config.categoryLink.linkText : '';

        return (
            <div className='ecomm-product-slider-top row'>
                <div className='ecomm-product-slider-title col-9 col-sm-6'>
                    <p>{config.title}</p>
                </div>
                <div className='ecomm-product-slider-nav col-3 col-sm-6'>
                    <a href={categoryLink}>{linkText}</a>
                </div>
            </div>
        );
    }

    private _renderSubCategories(): JSX.Element | null {
        const { subCategories } = this.props;

        return (
            <div className='ecomm-product-slider-categories'>
                {subCategories.map((item: CategoryHierarchy, index: number) => (
                    <SliderFilter key={index} category={item} callback={this._handleOnClickCategory} />
                ))}
            </div>
        );
    }

    private _handleOnClickCategory = (value: number) => {
        this.setState({ currCategoryId: value });
    };

    private _updateDimensions = () => {
        this.setState({ width: window.innerWidth });
    };
}

export default EcommProductSliderView;
